'use client';

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, X } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { updateMe } from '@/api/auth';
import { getOrgSettings, updateOrgSettings } from '@/api/organization';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

function UsageBar({ used, limit }: { used: number; limit: number }) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <div
        className={cn('h-full rounded-full transition-all', pct >= 90 ? 'bg-destructive' : 'bg-primary')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function SettingsPage() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'organization' ? 'organization' : 'profile';

  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();

  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // LLM key state
  const [llmKeyInput, setLlmKeyInput] = useState('');
  const [editingLlmKey, setEditingLlmKey] = useState(false);

  const isDirty = name !== (user?.name || '');

  const { data: orgSettings } = useQuery({
    queryKey: ['org-settings'],
    queryFn: getOrgSettings,
    placeholderData: {
      id: '', name: '', slug: '', plan: 'free', failure_mode: 'closed', subject_verification: 'none',
      llm_provider: 'openai', llm_api_key_configured: false,
      llm_fallback_credits_used: 0, llm_fallback_credits_limit: 100,
      llm_key_uses: 0, llm_key_query_cap: null, llm_fallback_available: false,
    },
  });

  const saveLlmKeyMutation = useMutation({
    mutationFn: (key: string) => updateOrgSettings({ llm_api_key: key }),
    onSuccess: () => {
      setEditingLlmKey(false);
      setLlmKeyInput('');
      void queryClient.invalidateQueries({ queryKey: ['org-settings'] });
    },
  });

  const clearLlmKeyMutation = useMutation({
    mutationFn: () => updateOrgSettings({ clear_llm_api_key: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-settings'] });
    },
  });

  // Verified end-user subject mode (open_tasks 0oo). This is the page the
  // router renders for both /settings and /organization (the latter redirects
  // here). A separate OrganizationPage once existed, unrouted; it was deleted
  // after it silently swallowed two edits (open_tasks 0uu).
  const saveSubjectVerificationMutation = useMutation({
    mutationFn: (mode: 'none' | 'verified_token') =>
      updateOrgSettings({ subject_verification: mode }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-settings'] });
    },
  });

  async function handleSave() {
    if (!isDirty || isSaving) return;
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      const updated = await updateMe({ name: name.trim() });
      setUser(updated);
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }

  function handleNameChange(value: string) {
    setName(value);
    setSaveSuccess(false);
  }

  const plan = user?.organization?.plan ?? 'free';
  const creditsUsed = orgSettings?.llm_fallback_credits_used ?? 0;
  const creditsLimit = orgSettings?.llm_fallback_credits_limit ?? 100;
  const creditsRemaining = creditsLimit - creditsUsed;

  return (
    <div className="space-y-6 max-w-2xl animate-fade-in">
      <PageHeader eyebrow="Account" title="Settings" />

      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="organization">Organization</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="rounded-lg border p-6 space-y-4">
            <h2 className="text-lg font-semibold">Profile</h2>
            <div className="flex items-center gap-4 pb-4 border-b">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xl font-semibold">
                {(user?.name || 'U').charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-semibold">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <Badge variant="outline" className="mt-1 capitalize">{user?.role?.replace('_', ' ')}</Badge>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Display Name</label>
              <Input value={name} onChange={(e) => handleNameChange(e.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input value={user?.email || ''} disabled />
              <p className="text-xs text-muted-foreground">Contact your organization admin to change your email.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <Input value={user?.role?.replace('_', ' ') || ''} disabled className="capitalize" />
            </div>
            {saveError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-md p-2">{saveError}</p>
            )}
            {saveSuccess && (
              <p className="text-sm text-green-600 bg-green-50 rounded-md p-2">Changes saved successfully.</p>
            )}
            <Button onClick={handleSave} disabled={!isDirty || isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="organization">
          <div className="space-y-4">
            <div className="rounded-lg border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Organization Details</h2>
                <Badge variant="outline" className="capitalize">{plan} plan</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Organization Name</label>
                  <Input value={user?.organization?.name || ''} disabled />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Slug</label>
                  <Input value={user?.organization?.slug || ''} disabled className="font-mono" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Organization ID</label>
                <Input value={user?.organization?.id || ''} disabled className="font-mono text-xs" />
              </div>
            </div>

            <div className="rounded-lg border p-6 space-y-4">
              <h2 className="text-lg font-semibold">Your Membership</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Your Role</p>
                  <p className="font-medium capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Member Since</p>
                  <p className="font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—'}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-6 space-y-3">
              <h2 className="text-lg font-semibold">Plan</h2>
              <p className="text-sm text-muted-foreground">
                Your organization is on the <span className="font-medium capitalize text-foreground">{plan}</span> plan.
              </p>
              <Button variant="outline" onClick={() => window.location.href = '/usage-billing'}>
                Manage Plan & Billing
              </Button>
            </div>

            {/* Retrieval subject verification (open_tasks 0oo) */}
            <div className="rounded-lg border bg-card p-6 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold">Retrieval subject verification</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Every retrieval names a principal. By default Gateco trusts the calling application
                    to name the right one. Requiring a verified end-user token makes Gateco check that
                    claim against your identity provider and refuse any retrieval where the token names
                    someone else.
                  </p>
                </div>
                <select
                  aria-label="Subject verification mode"
                  className="h-8 shrink-0 rounded-md border bg-background px-2 text-sm"
                  value={orgSettings?.subject_verification ?? 'none'}
                  disabled={saveSubjectVerificationMutation.isPending}
                  onChange={(e) =>
                    saveSubjectVerificationMutation.mutate(e.target.value as 'none' | 'verified_token')
                  }
                >
                  <option value="none">Trust the caller (default)</option>
                  <option value="verified_token">Require a verified end-user token</option>
                </select>
              </div>
              <p className="text-xs text-muted-foreground">
                {orgSettings?.subject_verification === 'verified_token'
                  ? 'Every retrieval must carry X-End-User-Token from an identity provider with OIDC settings configured. Agents and batch jobs with no signed-in user will be refused.'
                  : 'Tokens are optional but verified when sent. Watch the "asserted by" column on Secured Retrievals before switching this on.'}
              </p>
            </div>

            {/* LLM API Key for Answer Synthesis */}
            <div className="rounded-lg border p-6 space-y-4">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-lg font-semibold">Answer Synthesis</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Add your OpenAI API key to power Answer Synthesis and the Access Simulator with your own account, with no usage limits.
              </p>

              {/* Fallback credit status for paid plans */}
              {plan !== 'free' && !orgSettings?.llm_api_key_configured && (
                <div className="space-y-2">
                  {orgSettings?.llm_fallback_available ? (
                    <p className="text-sm text-muted-foreground">
                      Using Gateco's shared key.{' '}
                      <span className="font-medium text-foreground">{creditsRemaining}</span> of {creditsLimit} free calls remaining. Add your own key to remove this limit.
                    </p>
                  ) : (
                    <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2">
                      <p className="text-sm text-destructive font-medium">
                        Free synthesis credits exhausted. Add your OpenAI API key to continue using Answer Synthesis.
                      </p>
                    </div>
                  )}
                  <UsageBar used={creditsUsed} limit={creditsLimit} />
                </div>
              )}

              {plan === 'free' && !orgSettings?.llm_api_key_configured && (
                <p className="text-sm text-muted-foreground">
                  Answer synthesis requires your own OpenAI API key on the free plan.
                </p>
              )}

              {/* Key status indicator */}
              <div className="flex items-center gap-2">
                <div className={cn(
                  'h-2 w-2 rounded-full flex-shrink-0',
                  orgSettings?.llm_api_key_configured ? 'bg-green-500' : 'bg-muted-foreground',
                )} />
                <span className="text-sm">
                  {orgSettings?.llm_api_key_configured ? 'OpenAI key configured' : 'No key configured'}
                </span>
                {orgSettings?.llm_api_key_configured && (
                  <button
                    className="ml-auto text-xs text-muted-foreground hover:text-destructive transition-colors"
                    onClick={() => clearLlmKeyMutation.mutate()}
                    disabled={clearLlmKeyMutation.isPending}
                  >
                    Remove key
                  </button>
                )}
              </div>

              {/* Key input */}
              {editingLlmKey ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    placeholder="sk-..."
                    value={llmKeyInput}
                    onChange={(e) => setLlmKeyInput(e.target.value)}
                    className="font-mono text-sm"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    disabled={!llmKeyInput.startsWith('sk-') || saveLlmKeyMutation.isPending}
                    onClick={() => saveLlmKeyMutation.mutate(llmKeyInput)}
                  >
                    {saveLlmKeyMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <button onClick={() => { setEditingLlmKey(false); setLlmKeyInput(''); }}>
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingLlmKey(true)}
                >
                  {orgSettings?.llm_api_key_configured ? 'Replace key' : 'Add OpenAI API key'}
                </Button>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
